import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import generateUuid from 'uuid/v4'
import { nth, map, reduce, some, isEmpty, last } from 'lodash'

import { makeExemptionOverridesFromOrder } from '../../../utils/exemptionUtils'
import { centsToDollars, stringFloatToCents } from '../../../utils/formatters'
import PaymentTypes from '../../../utils/paymentTypes'


import { startCloseTabOrder, applyOrderExemptions, startTabOrderAdjustmentAndClose } from '../../../actions/order'

import { makeGetTabOrder } from '../../../selectors/order'
import { makeGetCurrentMenu } from '../../../selectors/menus'
import { makeGetDisplaySuiteByOrder, makeGetOrderPreferencesByOrder, makeGetActiveCustomerPreferences, getPaymentByOrder } from '../../../selectors/suites'
import { getPrinters } from '../../../selectors/printer'
import { getReceiptPrinterUuid } from '../../../selectors/config'

import { TabOrderDetailsViewModel, getGroupedItems } from '../../../viewmodels/TabOrderViewModel'

import { splitOrder } from '../../../actions/order'
import { transferOrder } from '../../../actions/orders/transfer'
import { printOrder, fetchPrinters } from '../../../actions/printer'

import { SIGNATURE_THRESHOLD_AMOUNT, USE_RICH_CHECKOUT } from '../../../constants'

import Close from '../../../components/tabs/OrderDetailsModal/Close'
import { getPeripheralStatus } from '../../../selectors/peripheral'
import { getDiscountsAvailable } from '../../../selectors/promotion'
import { makeGetActiveRevenueCenter, makeGetNoOpTenders } from '../../../selectors/revenueCenter'

const getPaymentData = (order) => {
  const paymentInfo = order?.paymentInfo ?? []
  const validPayments = paymentInfo?.filter?.(payment => payment.amountInCents > 0)
  return last(validPayments) || {}
}

const getSelectedSplitOrderItems = (selectedItemIndexes, items) => {
  const selectedSplitOrderItems = map(selectedItemIndexes, (index) => nth(items, index))
  const subtotal = reduce(selectedSplitOrderItems, (acc, item) => {
    const parentItemFractionalQuantity = item?.fractionalQuantity ?? 1
    const modifiersSubtotal = reduce(item?.modifiers, (acc, modifier) => {
      return acc + parentItemFractionalQuantity * (modifier?.priceInCents ?? 0)
    }, 0)

    return acc + modifiersSubtotal + parentItemFractionalQuantity * (item?.priceInCents ?? 0)
  }, 0)

  return { items: selectedSplitOrderItems, subtotal }
}

const closeOrderMapStateToProps = (state, ownProps) => {
  const orderId = ownProps?.match?.params?.orderId ?? ''
  const getTabOrder = makeGetTabOrder()
  const getDisplaySuite = makeGetDisplaySuiteByOrder()
  const suiteInformation = getDisplaySuite(state, orderId)
  const tabOrder = getTabOrder(state, orderId)
  const order = TabOrderDetailsViewModel(tabOrder)

  const userNotes = order?.userNotes ?? ''
  const customerId = order?.customerId ?? ''
  const revenueCenterId = order?.revenueCenterUuid ?? ''
  const isOrderLatestFromServer = tabOrder?.latestFromServer ?? false
  const isSuiteCustomer = !!customerId && !!revenueCenterId
  const getOrderPreferencesByOrder = makeGetOrderPreferencesByOrder()
  const printers = getPrinters(state)
  const deviceReceiptPrinterUuid = getReceiptPrinterUuid(state)
  const orderTotalFromOrder = order?.orderTotalFromOrder
  let paymentOptions = []
  let paymentOnOrder = undefined
  const paymentId = getPaymentData(tabOrder)?.uuid

  if (isSuiteCustomer) {
    const orderingPreference = getOrderPreferencesByOrder(state, orderId)
    const subtype = order?.orderType
    paymentOnOrder = getPaymentByOrder(state, orderId)
    paymentOptions = orderingPreference?.[subtype]?.payments ?? []
  }

  const amountDisplay = orderTotalFromOrder?.subtotalDisplay
  const isRichCheckoutEnabled = USE_RICH_CHECKOUT === 'true'
  const authorizationFailed = order?.orderState === 'authorization_failed'
  const peripheralStatus = getPeripheralStatus(state)
  const discountsAvailable = getDiscountsAvailable(state)
  const cashEnabled = makeGetActiveRevenueCenter()(state)?.allowCashPaymentsOnPos ?? true
  const noOpPayments = makeGetNoOpTenders()(state)
  const getMenu = makeGetCurrentMenu()
  const menu = getMenu(state)
  const signatureRequired = SIGNATURE_THRESHOLD_AMOUNT === 0 || (SIGNATURE_THRESHOLD_AMOUNT && order?.totalAmountInCents >= SIGNATURE_THRESHOLD_AMOUNT)
  const isNoTipMenu = isEmpty(menu?.tipSuggestions)
  const deferClose = !isNoTipMenu || signatureRequired

  // Exemptions for the close modal ALWAYS come from the order's current state.
  return {
    ...order,
    exemptionOverrides: makeExemptionOverridesFromOrder(order),
    customerId,
    amountDisplay,
    isOrderLatestFromServer,
    isRichCheckoutEnabled,
    paymentOnOrder,
    paymentOptions,
    printers,
    peripheralStatus,
    noOpPayments,
    orderTotalFromOrder,
    revenueCenterId,
    suiteInformation,
    deviceReceiptPrinterUuid,
    authorizationFailed,
    userNotes,
    paymentId,
    discountsAvailable,
    cashEnabled,
    deferClose,
  }
}

const splitOrderMapStateToProps = (state, ownProps) => {
  const historyState = ownProps?.history?.location?.state
  const splitOrderType = historyState?.splitOrderType ?? ''
  const selectedItemIndexes = historyState?.selectedItemIndexes ?? []
  const isFractionalSplit = historyState?.isFractionalSplit ?? false
  const fractionalQuantity = historyState?.fractionalQuantity ?? 1
  const splitTotal = historyState?.splitTotal ?? '$0.00'

  const closeOrderProps = closeOrderMapStateToProps(state, ownProps)
  const orderItems = closeOrderProps.items
  const { items, subtotal } = getSelectedSplitOrderItems(selectedItemIndexes, orderItems)
  const groupedItems = getGroupedItems({ itemModels: items }).map((item) => {
    return {
      ...item,
      priceInCents: item.priceInCents,
      price: centsToDollars(item.priceInCents),
      fractionalQuantity: fractionalQuantity * item.quantity,
      quantity: item.quantity,
    }
  })

  const splittableAmount = subtotal * fractionalQuantity
  const amount = centsToDollars(splittableAmount)

  return {
    ...closeOrderProps,
    fractionalQuantity,
    amount,
    splittableAmount,
    groupedItems,
    amountDisplay: closeOrderProps?.amountDisplay ?? amount,
    hidePayment: true,
    hideModifiedAt: true,
    isSplitOrder: true,
    isFractionalSplit,
    isRichCheckoutEnabled: false,
    items,
    needsRemoteOrderTotal: true,
    selectedItemIndexes,
    splitOrderType,
    subtotal: amount,
    splitTotal,
  }
}

const transferOrderMapStateToProps = (state, ownProps) => {
  const historyState = ownProps?.history?.location?.state
  const customerId = historyState?.customerId
  const splitOrderType = historyState?.splitOrderType ?? ''
  const closeOrderProps = closeOrderMapStateToProps(state, ownProps)
  const { orderType, revenueCenterId } = closeOrderProps
  const getActiveCustomerPreference = makeGetActiveCustomerPreferences()
  const orderingPreference = getActiveCustomerPreference(state, customerId, revenueCenterId)
  const paymentOptions = orderingPreference?.[orderType]?.payments ?? []

  let { paymentOnOrder } = closeOrderProps
  if (!isEmpty(paymentOnOrder) && !some(paymentOptions, (payment) => payment?.uuid === paymentOnOrder?.uuid)) {
    paymentOnOrder = {}
  }

  return { ...closeOrderProps, paymentOnOrder, customerId, paymentOptions, splitOrderType }
}

const mapStateToProps = (state, ownProps) => {
  const historyState = ownProps?.history?.location?.state

  if (!historyState) {
    return closeOrderMapStateToProps(state, ownProps)
  } else if (historyState.customerId) {
    return transferOrderMapStateToProps(state, ownProps)
  } else {
    return splitOrderMapStateToProps(state, ownProps)
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const orderId = ownProps?.match?.params?.orderId ?? ''
  const historyState = ownProps?.history?.location?.state
  const splitOrderType = historyState?.splitOrderType
  const transferCustomerId = historyState?.customerId
  const selectedItemIndexes = historyState?.selectedItemIndexes ?? []
  const splitTotalInCents = stringFloatToCents((historyState?.splitTotal ?? '$0.00').slice(1))
  const replace = ownProps?.history?.replace ?? function(){}
  const push = ownProps?.history?.push ?? function(){}
  let hoistedNewOrderUuid = undefined

  return {
    onClose: () => {
      replace('/tabs')
    },
    onFinalize: () => {
      replace(`/tabs/${orderId}/finalize`)
    },
    onStoredCardPayment: (exemptionOverrides, frozen, authorizationFailed, deferClose) => { // cant do a split order with a stored card
      if (authorizationFailed) {
        dispatch(startTabOrderAdjustmentAndClose({ orderId }))
      } else if (transferCustomerId) {
        dispatch(transferOrder({ orderId, transferCustomerId, paymentType: 'freedompay_credit', exemptionOverrides }))
        if (splitOrderType === 'tab') {
          replace(`/tabs/${orderId}`)
        }
      } else {
        dispatch(applyOrderExemptions(orderId, exemptionOverrides))
        if (deferClose) {
          push(`/tabs/${orderId}/close/actions`)
        } else {
          dispatch(startTabOrderAdjustmentAndClose({ orderId }))
        }
      }
    },
    onNewCardPayment: (cardReaderData, exemptionOverrides, items, newOrderUuid, isFractionalSplit, fractionalQuantity, splittableAmount, frozen, deferClose) => {
      if (transferCustomerId) {
        dispatch(transferOrder({ orderId, transferCustomerId, cardReaderData, paymentType: 'freedompay_credit', exemptionOverrides }))
        replace(`/tabs/${orderId}`)
      } else if (splitOrderType === undefined) {
        dispatch(applyOrderExemptions(orderId, exemptionOverrides))

        dispatch(startCloseTabOrder({ orderId, cardReaderData, newCard: true, deferClose}))
        
        if (deferClose) {
          push(`/tabs/${orderId}/close/actions`)
        } 
      } else {
        hoistedNewOrderUuid = newOrderUuid

        dispatch(splitOrder({
          cardReaderData,
          exemptionOverrides,
          lineItemIds: map(items, 'lineItemUuid'),
          newOrderUuid: hoistedNewOrderUuid,
          orderId,
          paymentType: 'freedompay_credit',
          fractionalQuantity,
          isFractionalSplit,
          splittableAmount,
          splitTotalInCents,
        }))

        if (splitOrderType === 'tab') {
          replace(`/tabs/${hoistedNewOrderUuid}`)
        } else {
          push(`/tabs/${hoistedNewOrderUuid}/close/actions`)
        }
      }
    },
    onCashPayment: (exemptionOverrides, items, isFractionalSplit, fractionalQuantity, splittableAmount) => {
      if (transferCustomerId) {
        dispatch(transferOrder({ orderId, transferCustomerId, paymentType: 'cash', exemptionOverrides }))
        replace(`/change-calculator/${orderId}?tab`)
      } else if (splitOrderType === undefined) {
        dispatch(applyOrderExemptions(orderId, exemptionOverrides))
        replace(`/change-calculator/${orderId}?tab`)
      } else { // split order
        hoistedNewOrderUuid = generateUuid()

        dispatch(splitOrder({
          exemptionOverrides,
          lineItemIds: map(items, 'lineItemUuid'),
          newOrderUuid: hoistedNewOrderUuid,
          orderId,
          paymentType: 'cash',
          fractionalQuantity,
          isFractionalSplit,
          splittableAmount,
          splitTotalInCents,
        }))

        replace({
          pathname: `/change-calculator/${hoistedNewOrderUuid}?tab`,
          state: {
            splitTotal: splitTotalInCents,
          },
        })
      }
    },
    onRemotePayment: (paymentPreference, exemptionOverrides, items, isFractionalSplit, fractionalQuantity, splittableAmount, deferClose, frozen) =>  {
      if (transferCustomerId) {
        const paymentType = PaymentTypes.nameFromInteger(paymentPreference?.paymentType)
        dispatch(transferOrder({ orderId, transferCustomerId, customerPaymentId: paymentPreference?.uuid, paymentType, exemptionOverrides }))
        if (deferClose) push(`/tabs/${orderId}/close/actions`)
        if (splitOrderType === 'tab') {
          replace(`/tabs/${orderId}`)
        }
      } else if (splitOrderType === undefined) {
        dispatch(applyOrderExemptions(orderId, exemptionOverrides))
        dispatch(startCloseTabOrder({ orderId, paymentPreference, remotePayment: true, deferClose: true }))
        push(`/tabs/${orderId}/close/actions`)
      } else { // split order
        hoistedNewOrderUuid = generateUuid()
        const lineItemIds = map(items, 'lineItemUuid')

        dispatch(splitOrder({
          paymentPreference,
          selectedItemIndexes,
          exemptionOverrides,
          orderId,
          newOrderUuid: hoistedNewOrderUuid,
          lineItemIds,
          fractionalQuantity,
          isFractionalSplit,
          splittableAmount,
          splitTotalInCents,
        }))

        if (splitOrderType === 'tab') {
          replace(`/tabs/${hoistedNewOrderUuid}`)
        } else if (splitOrderType === 'payNow') {
          replace(`/tabs/${hoistedNewOrderUuid}/close/actions`)
        }
      }
    },
    fetchPrinters: () => dispatch(fetchPrinters()),
    onViewCheck: () => {
      return push(`/signature/${orderId}?action=close`)
    },
    printOrder: (params) => dispatch(printOrder(params)),
    onNoOpPayment: (deferClose) => {
      dispatch(startCloseTabOrder({ orderId, remotePayment: true, deferClose }))
      if (deferClose) push(`/tabs/${orderId}/close/actions`)
    },
    onSelectNoOpPayment: (deferClose) => {
      push({
        pathname: `/tabs/${orderId}/close/select-payment`,
        state: {
          deferClose,
        }
      })
    }
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Close))
